import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import type { MetricKey } from '@/lib/financeGlossary'
import { cn } from '@/lib/utils'
import { TermLabel, useMoney } from './moneyUi'
import { hasNetSales, periodTotals, shareOf, type PeriodPoint } from './pnlStatementLogic'

type Props = {
  points: PeriodPoint[]
  /** "day", "week" — how one row reads in a sentence. */
  grainNoun: string
  formatPeriod: (iso: string) => string
  /** Refund columns appear only when the period has refunds. */
  showRefunds: boolean
}

type Column = {
  key: keyof Omit<PeriodPoint, 'period' | 'orders'>
  label: string
  metric?: MetricKey
  help?: string
  /** The column the row closes on: set in medium weight. */
  result?: boolean
}

const STAFF_MEALS_COLUMN: Column = {
  key: 'staffMeals',
  label: 'Staff meals (at cost)',
  help: 'Food cost of staff meals. Not sales; taken off profit here.',
}

const COLUMNS: Column[] = [
  { key: 'grossSales', label: 'Gross sales', metric: 'gross_sales' },
  {
    key: 'taxAndFees',
    label: 'Tax and fees',
    help: 'Sales tax collected for the tax authority, plus POS and fiscal fees. Gross sales less this is net sales.',
  },
  { key: 'netSales', label: 'Net sales', metric: 'net_sales' },
  { key: 'cogs', label: 'Cost of goods', metric: 'cogs' },
  { key: 'opex', label: 'Operating expenses', metric: 'operating_expenses' },
  { key: 'netProfit', label: 'Net profit', metric: 'net_profit', result: true },
]

/** Past this many rows the table scrolls inside its card. */
const LONG_TABLE = 40

const REFUND_COLUMNS: Column[] = [
  { key: 'refunds', label: 'Refunds (excl. tax)', metric: 'refunds_issued' },
  { key: 'netAfterRefunds', label: 'After refunds', metric: 'net_profit_after_refunds', result: true },
]

/**
 * The period table (F-FE-N164), and the chart's table view. Figures are ink;
 * a loss is red; a zero is a dash, so the days something happened stand out
 * from the days nothing did. Reading a row left to right is the statement:
 * gross sales, less tax and fees, is net sales; less the two costs, is profit.
 */
export function PeriodBreakdownTable({ points, grainNoun, formatPeriod, showRefunds }: Props) {
  const money = useMoney()
  // F-MM-N30: the staff-meals column only when some row has one.
  const base = points.some(p => Math.abs(p.staffMeals) > 0.005)
    ? COLUMNS.flatMap(c => (c.key === 'opex' ? [c, STAFF_MEALS_COLUMN] : [c]))
    : COLUMNS
  const columns = showRefunds ? [...base, ...REFUND_COLUMNS] : base
  const totals = periodTotals(points)

  const cell = (v: number, col: Column) => {
    const zero = Math.abs(v) < 0.005
    const loss = col.result && v < -0.005
    return (
      <td
        key={col.key}
        className={cn(
          'whitespace-nowrap border-b px-3 py-2 text-right tabular-nums last:pr-6 group-hover:bg-muted/50',
          zero ? 'text-muted-foreground/60' : loss ? 'text-danger-fg' : 'text-foreground',
          col.result && !zero && 'font-medium',
        )}
      >
        {zero ? '\u2013' : money.plain(v)}
      </td>
    )
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Period breakdown</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          One row per {grainNoun}, busiest and quietest alike. Amounts in {money.symbol}.
        </p>
      </CardHeader>
      {points.length === 0 ? (
        <p className="px-6 pb-10 pt-4 text-center text-sm text-muted-foreground">No data for this period</p>
      ) : (
        // A month of days reads straight down the page; only a long run of
        // hours gets its own scroll, with the header and totals pinned.
        <div className={cn('overflow-auto border-t', points.length > LONG_TABLE && 'max-h-[640px]')}>
          <table className="w-full min-w-[820px] border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 top-0 z-20 border-b bg-muted px-6 py-2.5 text-left align-bottom font-medium text-muted-foreground"
                >
                  Period
                </th>
                <th scope="col" className="sticky top-0 z-10 border-b bg-muted px-3 py-2.5 text-right align-bottom font-medium text-muted-foreground">
                  <TermLabel label="Orders" metric="orders" />
                </th>
                {columns.map(c => (
                  <th
                    key={c.key}
                    scope="col"
                    className="sticky top-0 z-10 border-b bg-muted px-3 py-2.5 text-right align-bottom font-medium leading-tight text-muted-foreground last:pr-6"
                  >
                    <TermLabel label={c.label} metric={c.metric} help={c.help} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map(p => (
                <tr key={p.period} className="group">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 whitespace-nowrap border-b bg-card px-6 py-2 text-left font-normal text-muted-foreground group-hover:bg-muted"
                  >
                    {formatPeriod(p.period)}
                  </th>
                  <td
                    className={cn(
                      'border-b px-3 py-2 text-right tabular-nums group-hover:bg-muted/50',
                      p.orders === 0 ? 'text-muted-foreground/60' : 'text-foreground',
                    )}
                  >
                    {p.orders === 0 ? '–' : p.orders}
                  </td>
                  {columns.map(c => cell(p[c.key], c))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th
                  scope="row"
                  className="sticky bottom-0 left-0 z-20 whitespace-nowrap border-t  bg-muted px-6 py-2.5 text-left font-medium text-foreground"
                >
                  Total
                  <span className="ml-2 font-normal text-muted-foreground">
                    {points.length} {points.length === 1 ? grainNoun : `${grainNoun}s`}
                  </span>
                </th>
                <td className="sticky bottom-0 z-10 border-t bg-muted px-3 py-2.5 text-right align-top font-medium tabular-nums">{totals.orders}</td>
                {columns.map(c => (
                  <td
                    key={c.key}
                    className={cn(
                      'sticky bottom-0 z-10 whitespace-nowrap border-t bg-muted px-3 py-2.5 text-right align-top font-medium tabular-nums last:pr-6',
                      c.result && totals[c.key] < -0.005 ? 'text-danger-fg' : 'text-foreground',
                    )}
                  >
                    {money.plain(totals[c.key])}
                    {c.result && hasNetSales(totals.netSales) && (
                      <span className="block text-xs font-normal text-muted-foreground">
                        {shareOf(totals[c.key], totals.netSales)} of net sales
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  )
}
